import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Download, ExternalLink, GraduationCap, Lightbulb, Loader2, Pencil, Plus, Search, ThumbsDown, ThumbsUp, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

// Panel admin > Vanta AI > Entraînement : les exemples qui apprennent à Vector à
// répondre comme toi (question → réponse idéale), les réponses notées 👍/👎 sur /ai
// à corriger, l'import et l'export du fichier pour le carnet Google Colab.

const COLAB_URL = "https://colab.research.google.com/github/TilyesTAG/VantaPulse---Site/blob/main/vanta-ai/train/Vanta_AI_Colab.ipynb";
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const area = "w-full rounded-lg bg-black/20 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/25 resize-y";
const SOURCES = { manual: "écrit", feedback: "avis corrigé", import: "importé", lesson: "leçon apprise" };

function ExampleEditor({ initial, onSave, onCancel, saveLabel = "Enregistrer" }) {
  const [prompt, setPrompt] = useState(initial?.prompt || "");
  const [answer, setAnswer] = useState(initial?.answer || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave({ prompt: prompt.trim(), answer: answer.trim() }); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="vai-training-editor">
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} required placeholder="Ce que demande l'utilisateur (ex. : Comment j'installe VantaPhone ?)" className={area} />
      <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={6} required placeholder="La réponse idéale de Vector, exactement comme tu veux qu'il réponde (Markdown accepté)" className={area} />
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Annuler</Button>}
        <Button type="submit" size="sm" disabled={busy || !prompt.trim() || !answer.trim()}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}{saveLabel}</Button>
      </div>
    </form>
  );
}

function FeedbackItem({ item, onReview }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="px-3 py-3 space-y-2" data-testid="vai-feedback-item">
      <div className="flex flex-wrap items-center gap-2 text-xs text-white/45">
        {item.rating === "up" ? <ThumbsUp className="w-3.5 h-3.5 text-emerald-300" /> : <ThumbsDown className="w-3.5 h-3.5 text-red-300" />}
        <span>{item.rated_at ? new Date(item.rated_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
        {item.model && <span>{item.model}</span>}
        {item.note && <span className="text-amber-200/90">« {item.note} »</span>}
      </div>
      {editing ? (
        <ExampleEditor initial={{ prompt: item.question, answer: item.answer }} saveLabel="Ajouter à l'entraînement" onCancel={() => setEditing(false)}
          onSave={(ex) => onReview(item, { action: "add", ...ex })} />
      ) : (
        <>
          <p className="text-sm"><span className="text-white/40">Question : </span>{item.question || <em className="text-white/30">introuvable</em>}</p>
          <p className="text-sm text-white/70 whitespace-pre-wrap line-clamp-6"><span className="text-white/40">Réponse : </span>{item.answer || <em className="text-white/30">vide</em>}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setEditing(true)} disabled={!item.question}><Pencil className="w-3.5 h-3.5 mr-1" />{item.rating === "down" ? "Corriger et ajouter" : "Vérifier et ajouter"}</Button>
            <Button size="sm" variant="ghost" onClick={() => onReview(item, { action: "dismiss" })}>Ignorer</Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function VantaAITraining({ hdr }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [feedback, setFeedback] = useState([]);
  const [lessons, setLessons] = useState(null);
  const [filter, setFilter] = useState("down");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async (search = q, skip = 0) => {
    try {
      const r = await axios.get(`${API}/admin/vai/training`, { headers: hdr(), params: { q: search, skip } });
      setData((d) => (skip && d ? { ...r.data, examples: [...d.examples, ...r.data.examples] } : r.data));
    } catch (err) { toast.error(errorText(err, "Chargement impossible")); }
  }, [hdr]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadFeedback = useCallback(async (rating = filter) => {
    try {
      const r = await axios.get(`${API}/admin/vai/training/feedback`, { headers: hdr(), params: { rating } });
      setFeedback(r.data.items);
    } catch (err) { toast.error(errorText(err, "Chargement des avis impossible")); }
  }, [hdr]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadLessons = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/vai/lessons`, { headers: hdr() });
      setLessons(r.data);
    } catch (err) { toast.error(errorText(err, "Chargement des leçons impossible")); }
  }, [hdr]);
  useEffect(() => { load(""); loadFeedback("down"); loadLessons(); }, [load, loadFeedback, loadLessons]);
  const trainLesson = async (lesson) => {
    try {
      await axios.post(`${API}/admin/vai/lessons/${lesson.id}/train`, {}, { headers: hdr() });
      setLessons((l) => ({ ...l, lessons: l.lessons.map((x) => (x.id === lesson.id ? { ...x, trained: true } : x)) }));
      toast.success("Ajoutée à l'entraînement");
      load();
    } catch (err) { toast.error(errorText(err, "Impossible")); }
  };
  const forgetLesson = async (lesson) => {
    if (!window.confirm("Oublier cette leçon ? Vanta AI ne s'en servira plus.")) return;
    try {
      await axios.delete(`${API}/admin/vai/lessons/${lesson.id}`, { headers: hdr() });
      setLessons((l) => ({ total: l.total - 1, lessons: l.lessons.filter((x) => x.id !== lesson.id) }));
    } catch (err) { toast.error(errorText(err, "Suppression impossible")); }
  };

  const add = async (ex) => {
    try {
      await axios.post(`${API}/admin/vai/training`, ex, { headers: hdr() });
      toast.success("Exemple ajouté");
      setAdding(false);
      load();
    } catch (err) { toast.error(errorText(err, "Ajout impossible")); }
  };
  const save = async (id, ex) => {
    try {
      const r = await axios.put(`${API}/admin/vai/training/${id}`, ex, { headers: hdr() });
      setData((d) => ({ ...d, examples: d.examples.map((x) => (x.id === id ? r.data : x)) }));
      setEditId(null);
      toast.success("Exemple modifié");
    } catch (err) { toast.error(errorText(err, "Modification impossible")); }
  };
  const remove = async (ex) => {
    if (!window.confirm("Supprimer cet exemple ?")) return;
    try {
      await axios.delete(`${API}/admin/vai/training/${ex.id}`, { headers: hdr() });
      setData((d) => ({ ...d, total: d.total - 1, matching: d.matching - 1, examples: d.examples.filter((x) => x.id !== ex.id) }));
    } catch (err) { toast.error(errorText(err, "Suppression impossible")); }
  };
  const review = async (item, body) => {
    try {
      await axios.post(`${API}/admin/vai/training/feedback/${item.message_id}`, body, { headers: hdr() });
      setFeedback((list) => list.filter((x) => x.message_id !== item.message_id));
      if (body.action === "add") toast.success("Ajouté à l'entraînement");
      load();
    } catch (err) { toast.error(errorText(err, "Impossible")); }
  };
  const exportFile = async () => {
    setBusy("export");
    try {
      const r = await axios.get(`${API}/admin/vai/training/export`, { headers: hdr(), responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "vector-entrainement.jsonl";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const detail = err?.response?.data instanceof Blob ? JSON.parse(await err.response.data.text()).detail : null;
      toast.error(detail || "Export impossible");
    } finally { setBusy(""); }
  };
  const importFile = async (file) => {
    if (!file) return;
    setBusy("import");
    try {
      const r = await axios.post(`${API}/admin/vai/training/import`, { jsonl: await file.text() }, { headers: hdr() });
      toast.success(`${r.data.added} exemple(s) importé(s)${r.data.skipped ? `, ${r.data.skipped} ligne(s) ignorée(s)` : ""}`);
      load();
    } catch (err) { toast.error(errorText(err, "Import impossible")); } finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!data) return <div className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;
  const pct = Math.min(100, Math.round((data.total / data.target) * 100));

  return (
    <div className="space-y-5" data-testid="vai-training">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium flex items-center gap-2"><GraduationCap className="w-4 h-4" />Ton Vector : {data.total} exemple{data.total > 1 ? "s" : ""}</p>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".jsonl,.json,.txt" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!!busy}>{busy === "import" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Importer (.jsonl)</Button>
            <Button size="sm" onClick={exportFile} disabled={!!busy || !data.total} data-testid="vai-training-export">{busy === "export" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Exporter pour l'entraînement</Button>
          </div>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-500 to-violet-500" style={{ width: `${pct}%` }} /></div>
        <p className="text-xs text-white/50">{data.total >= data.target ? "Assez d'exemples pour un bon entraînement. Plus il y en a (jusqu'à 2 000), mieux c'est." : `Vise au moins ${data.target} exemples variés avant le premier entraînement (FiveM, Discord, VantaPulse, code, refus des demandes dangereuses…).`}</p>
        <ol className="text-xs text-white/60 space-y-1 list-decimal pl-4">
          <li>Écris des exemples ci-dessous, et corrige les réponses notées 👎 par les utilisateurs.</li>
          <li><strong className="text-white/80">Exporter pour l'entraînement</strong> télécharge ton fichier.</li>
          <li>Ouvre le <a href={COLAB_URL} target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:underline inline-flex items-center gap-1">carnet Google Colab<ExternalLink className="w-3 h-3" /></a> (gratuit), choisis une carte graphique T4, puis « Exécuter tout » et ajoute le fichier quand il le demande.</li>
          <li>Sur le VPS : <code className="px-1 rounded bg-white/10">sudo bash /opt/vantapulse/deploy/vanta-model.sh install ton-compte/vector</code>. Ton Vector remplace le modèle de base (retour possible avec <code className="px-1 rounded bg-white/10">revert</code>).</li>
        </ol>
      </div>

      <div data-testid="vai-lessons">
        <p className="text-sm font-medium flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-300" />Leçons apprises des conversations{lessons ? ` (${lessons.total})` : ""}</p>
        <p className="text-xs text-white/45 mt-1 mb-2">Quand quelqu'un corrige Vanta AI (« non, c'est faux… »), il vérifie sur internet. Si les sources donnent raison à la personne, il garde la leçon et s'en sert pour tout le monde. Rien n'est appris sans sources.</p>
        {!lessons ? null : lessons.lessons.length === 0 ? <p className="text-sm text-white/40">Aucune leçon pour l'instant.</p> : (
          <div className="divide-y divide-white/5 border border-white/10 rounded-lg">
            {lessons.lessons.map((l) => (
              <div key={l.id} className="px-3 py-2.5 flex gap-3" data-testid="vai-lesson">
                <div className="min-w-0 flex-1">
                  <p className="text-sm break-words">{l.text}</p>
                  <p className="text-[11px] text-white/35 mt-1 flex flex-wrap gap-x-2">
                    {l.question && <span className="truncate max-w-[22rem]">Question : {l.question}</span>}
                    <span>servie {l.uses || 0} fois</span>
                    {(l.sources || []).slice(0, 2).map((src) => <a key={src.url} href={src.url} target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:underline truncate max-w-[14rem]">{src.title || src.url}</a>)}
                  </p>
                </div>
                <div className="flex items-start gap-1 shrink-0">
                  {l.trained ? <span className="text-[11px] text-emerald-300 px-1.5 py-1">Dans l'entraînement</span>
                    : <button type="button" onClick={() => trainLesson(l)} className="p-1.5 rounded-md text-white/45 hover:text-white hover:bg-white/10" title="Ajouter à l'entraînement" aria-label="Ajouter à l'entraînement"><GraduationCap className="w-4 h-4" /></button>}
                  <button type="button" onClick={() => forgetLesson(l)} className="p-1.5 rounded-md text-white/45 hover:text-red-300 hover:bg-white/10" title="Oublier" aria-label="Oublier la leçon"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <p className="text-sm font-medium">Avis des utilisateurs à traiter</p>
          <div className="flex gap-1 text-xs">
            {[["down", "👎 À corriger"], ["up", "👍 Bonnes réponses"], ["", "Tous"]].map(([id, label]) => (
              <button key={id || "all"} type="button" onClick={() => { setFilter(id); loadFeedback(id); }} className={`px-2.5 py-1 rounded-lg border ${filter === id ? "border-white/25 bg-white/10" : "border-white/10 text-white/55 hover:text-white"}`}>{label}</button>
            ))}
          </div>
        </div>
        {feedback.length === 0 ? <p className="text-sm text-white/40">Rien à traiter pour l'instant.</p> : (
          <div className="divide-y divide-white/5 border border-white/10 rounded-lg">
            {feedback.map((item) => <FeedbackItem key={item.message_id} item={item} onReview={review} />)}
          </div>
        )}
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <p className="text-sm font-medium mr-auto">Exemples</p>
          <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex items-center gap-2">
            <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-8 h-9 w-48" /></div>
          </form>
          <Button size="sm" onClick={() => setAdding(true)} disabled={adding} data-testid="vai-training-add"><Plus className="w-4 h-4 mr-1" />Ajouter un exemple</Button>
        </div>
        {adding && <div className="rounded-lg border border-white/15 bg-white/[0.03] p-3 mb-3"><ExampleEditor onSave={add} onCancel={() => setAdding(false)} saveLabel="Ajouter" /></div>}
        {data.examples.length === 0 ? <p className="text-sm text-white/40">{q ? "Aucun résultat." : "Aucun exemple pour l'instant : commence par « Ajouter un exemple » ou importe un fichier."}</p> : (
          <div className="divide-y divide-white/5 border border-white/10 rounded-lg">
            {data.examples.map((ex) => (
              <div key={ex.id} className="px-3 py-3 group" data-testid="vai-training-example">
                {editId === ex.id ? <ExampleEditor initial={ex} onSave={(v) => save(ex.id, v)} onCancel={() => setEditId(null)} /> : (
                  <div className="flex gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium break-words">{ex.prompt}</p>
                      <p className="text-sm text-white/60 whitespace-pre-wrap break-words line-clamp-4 mt-1">{ex.answer}</p>
                      <p className="text-[11px] text-white/30 mt-1">{SOURCES[ex.source] || ex.source}{ex.turns > 1 ? ` · ${ex.turns} échanges` : ""}</p>
                    </div>
                    <div className="flex items-start gap-1 shrink-0">
                      <button type="button" onClick={() => setEditId(ex.id)} className="p-1.5 rounded-md text-white/45 hover:text-white hover:bg-white/10" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
                      <button type="button" onClick={() => remove(ex)} className="p-1.5 rounded-md text-white/45 hover:text-red-300 hover:bg-white/10" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {data.examples.length < data.matching && (
          <div className="flex justify-center mt-3"><Button size="sm" variant="ghost" onClick={() => load(q, data.examples.length)}>Voir plus ({data.matching - data.examples.length})</Button></div>
        )}
        {q && <button type="button" onClick={() => { setQ(""); load(""); }} className="mt-2 text-xs text-white/45 hover:text-white inline-flex items-center gap-1"><X className="w-3 h-3" />Effacer la recherche</button>}
      </div>
    </div>
  );
}
